import React from "react";
import PublicNav from "../components/PublicNav";
import Footer from "../components/Footer";
import { useCms } from "../context/CmsContext";

function Initials({ name }) {
    const i = name.split(" ").map((p) => p[0]).slice(0, 2).join("");
    return (
        <div className="aspect-[4/3] bg-ink-700 flex items-center justify-center">
            <div className="font-serif text-7xl text-ice/40 tracking-wider">{i}</div>
        </div>
    );
}

export default function APropos() {
    const cms = useCms();
    const t = (k, fb) => cms.get(k, fb);

    const values = [
        {
            title: t("apropos.val1_title", "L'orientation est trop importante pour être laissée au hasard"),
            body: t("apropos.val1_body", "Face au manque d'information et d'accompagnement dont souffrent de nombreux étudiants en France, nous avons fait le choix d'un engagement de proximité, au service de chaque parcours."),
        },
        {
            title: t("apropos.val2_title", "Nous travaillons avec vous, pas à votre place"),
            body: t("apropos.val2_body", "Notre rôle est de vous aider à trouver vos propres réponses et non de vous donner les nôtres. Vous repartez avec un projet qui vient vraiment de vous."),
        },
        {
            title: t("apropos.val3_title", "Changer de voie, c'est courageux, ça n'est pas un échec !"),
            body: t("apropos.val3_body", "Se réorienter demande plus de lucidité que de continuer dans une direction qui ne convient pas. Nous sommes là pour vous aider à valoriser ce changement."),
        },
    ];

    const team = [
        {
            name: t("apropos.cecile_name", "Cécile Castex"),
            location: t("apropos.cecile_location", "Bordeaux"),
            photo: t("team.cecile_photo", "/assets/cecile.png"),
            zoom: 1.3,
            bio: t("apropos.cecile_bio", "Professeur dans l'enseignement supérieur, je consacre mon quotidien à accompagner les étudiants post-bac dans la construction de leur parcours. Mère de quatre enfants, je mesure combien les choix d'orientation sont déterminants. Cette double expérience pédagogique et familiale nourrit un engagement sincère : aider chaque jeune à identifier ses aspirations et à se donner les moyens de les concrétiser. Mon approche repose sur une conviction simple : un choix éclairé est le premier pas vers une vie accomplie."),
        },
        {
            name: t("apropos.cassandre_name", "Cassandre Colbeau-Justin"),
            location: t("apropos.cassandre_location", "Paris"),
            photo: t("team.cassandre_photo", "/assets/cassandre.png"),
            zoom: 1.5,
            bio: t("apropos.cassandre_bio", "Consultante en finance durable et stratégie, j'évolue dans un environnement exigeant où l'écoute, l'analyse et l'accompagnement sont essentiels. Très proche des problématiques d'orientation grâce à mon parcours récent et à celui de mes frères et sœurs actuellement confrontés à ces sujets, je mesure combien ces choix peuvent être complexes, stressants et parfois anxiogènes pour les jeunes comme pour leurs familles. Ma jeunesse est aujourd'hui un véritable atout : elle me permet de comprendre les doutes, les attentes et les aspirations des étudiants avec proximité et bienveillance."),
        },
    ];

    // We keep the styled hero heading static (commas in blue) but let the editor change the wording via apropos.hero_title.
    const heroTitle = t("apropos.hero_title", "Deux professionnelles, l'une à Paris, l'autre à Bordeaux, qui ont vraiment envie de vous aider.");

    return (
        <div data-testid="page-apropos" className="bg-ink-800 min-h-screen text-white">
            <PublicNav />

            <section className="pt-24 pb-[42px] lg:pt-32 lg:pb-[42px] px-6 md:px-12">
                <div className="max-w-5xl mx-auto">
                    <span className="font-serif text-2xl sm:text-3xl text-ice block mb-3">À Propos</span>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-2 leading-[1.1]">
                        Deux professionnelles,<br />
                        l'une à <span className="text-ice">Paris</span><span className="text-ice">,</span> l'autre à{" "}
                        <span className="text-ice">Bordeaux</span><span className="text-ice">,</span><br />
                        qui ont vraiment envie de vous aider.
                    </h1>
                </div>
            </section>

            <section className="px-6 md:px-12 py-[42px] border-t border-white/5">
                <div className="max-w-7xl mx-auto">
                    <span className="font-serif text-2xl sm:text-3xl text-ice block mb-3">Notre Équipe</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-10">
                        {team.map((m) => (
                            <article
                                key={m.name}
                                data-testid={`team-${m.name.split(" ")[0].toLowerCase()}`}
                                className="bg-ink-700 rounded-xl overflow-hidden border border-ice/15 flex flex-col"
                            >
                                {m.photo ? (
                                    <div className="pt-10 pb-2 px-8 flex justify-center">
                                        <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-2 border-ice/30 shadow-2xl">
                                            <img
                                                src={m.photo}
                                                alt={m.name}
                                                className="w-full h-full object-cover"
                                                style={{ transform: `scale(${m.zoom || 1})`, transformOrigin: "center 30%" }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <Initials name={m.name} />
                                )}
                                <div className="p-8 text-center">
                                    <span className="text-sm tracking-wide text-ice block">{m.location}</span>
                                    <h3 className="font-serif text-3xl mt-2">{m.name}</h3>
                                    <p className="mt-5 text-white/85 leading-relaxed text-base text-left whitespace-pre-line">{m.bio}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-[42px] px-6 md:px-12 bg-ink-700/30 border-t border-white/5">
                <div className="max-w-7xl mx-auto">
                    <span className="font-serif text-2xl sm:text-3xl text-ice block mb-3">Nos Valeurs</span>
                    <h2 className="font-serif text-3xl sm:text-4xl">
                        <span className="text-ice">Écouter</span> · <span className="text-ice">Accompagner</span> ·{" "}
                        <span className="text-ice">Guider</span> · <span className="text-ice">Motiver</span>
                    </h2>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
                        {values.map((v, i) => (
                            <div key={i} className="border-t border-ice/40 pt-6">
                                <h3 className="font-serif text-2xl leading-snug whitespace-pre-line">{v.title}</h3>
                                <p className="mt-4 text-white/80 leading-relaxed whitespace-pre-line">{v.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
